/* eslint-disable jsx-a11y/alt-text */
import { IonIcon, IonItem, IonRow, IonSpinner, IonText } from '@ionic/react';
import HStack from 'components/HStack';
import {
  calendarOutline,
  flagOutline,
  star,
  trophy,
  locationOutline,
  medal,
  person,
} from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { ST } from 'types';
import { useStores } from 'models/RootStore/RootStoreContext';

import './ExploreListItem.css';
import { ExploreElement } from 'models/ExploreElementModel/ExploreElementModel';
import { isMockElement } from '../../../util/mockDataDetection';

interface ExploreListItemProps {
  item: ExploreElement;
  isFriend: boolean;
  onOpenEventModal?: (element: ExploreElement) => void;
  onOpenRankingModal?: (element: ExploreElement) => void;
  onOpenPersonModal?: (element: ExploreElement, personScore: number) => void;
  onOpenContestModal?: (element: ExploreElement) => void;
}

export const scaleData = (
  value: number | undefined,
  suffix: string = ''
): string => {
  if (value === undefined) return `?? ${suffix}`;
  if (value < 1 && suffix === 'm') {
    suffix = 'cm';
    value *= 100;
  }

  return `${value.toFixed(2)} ${suffix}`;
};

export const calcHighestSpeed = (steps?: ST.PlayStep[]): number | undefined => {
  if (!steps) return undefined;

  return steps.reduce((highest: number, current: ST.PlayStep) => {
    const speed = typeof current.v_ms === 'number' ? current.v_ms * 3.6 : 0;
    return Math.max(highest, speed);
  }, 0);
};

// Helper: Wandelt Wert in Date-Objekt um, falls nötig
function parseDateOrDefault(value: any, fallback: Date = new Date()): Date {
  if (!value) return fallback;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }
  if (typeof value === 'object' && value.seconds !== undefined) {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
  }
  return fallback;
}

const ExploreListItem: React.FC<ExploreListItemProps> = observer(
  ({
    item,
    onOpenEventModal,
    onOpenRankingModal,
    onOpenPersonModal,
    onOpenContestModal,
  }) => {
    const [isLoading] = useState(false);
    const { currentUser } = useStores();
    const theTitle = item.name ?? <Trans>UNKNOWN_TITLE</Trans>;
    const renderEventContent = () => {
      const startDate = parseDateOrDefault(item.startDate);
      return (
        <>
          {/* Header with Title and Mock Pill */}
          <IonRow className="ion-justify-content-between ion-align-items-center">
            <div className="title-section">
              <IonText style={{ fontSize: 24, fontWeight: 'bold' }}>
                {theTitle}
              </IonText>
              {isMockElement(item) && <div className="mock-pill">PROXY</div>}
            </div>
          </IonRow>

          {/* Meta Information at Bottom */}
          <div className="meta-info-container">
            {/* Date für Events */}
            <HStack className="meta-date-row">
              <IonIcon icon={calendarOutline} />
              <IonText>
                {startDate.toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                })}
              </IonText>
            </HStack>
          </div>
        </>
      );
    };

    const renderRankingContent = () => {
      // Priorität: Verwende echte Ranking-Daten aus item.rankings
      let rankingEntries = [];

      if (item.rankings && item.rankings.length > 0) {
        // Echte Ranking-Daten vorhanden - diese verwenden
        rankingEntries = item.rankings.slice(0, 3).map((ranking) => ({
          position: ranking.position,
          name: ranking.userName || 'Unknown',
          score: ranking.score || 0,
        }));
      } else {
        rankingEntries = [
          { position: 1, name: 'Max Mustermann', score: 2450 },
          { position: 2, name: 'Anna Schmidt', score: 2380 },
          { position: 3, name: 'Tom Weber', score: 2290 },
        ];
      }

      const getPositionIcon = (position: number) => {
        switch (position) {
          case 1:
            return <IonIcon icon={trophy} className="position-icon gold" />;
          case 2:
            return <IonIcon icon={medal} className="position-icon silver" />;
          case 3:
            return <IonIcon icon={medal} className="position-icon bronze" />;
          default:
            return <span className="position-number">{position}</span>;
        }
      };

      return (
        <>
          {/* Header with Title and Mock Pill */}
          <IonRow className="ion-justify-content-between ion-align-items-center">
            <div className="title-section">
              <IonText style={{ fontSize: 24, fontWeight: 'bold' }}>
                {theTitle}
              </IonText>
              {isMockElement(item) && <div className="mock-pill">SPINNAX</div>}
            </div>
          </IonRow>

          {/* Meta Information at Bottom */}
          <div className="meta-info-container">
            {/* Top 3 Ranking Preview */}
            <div className="ranking-preview">
              {rankingEntries.map((entry) => (
                <div key={entry.position} className="ranking-preview-item">
                  <div className="ranking-position">
                    {getPositionIcon(entry.position)}
                  </div>
                  <div className="ranking-name">{entry.name}</div>
                  <div className="ranking-score">{entry.score}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    };

    // Extrahiere den echten Score aus der description oder verwende einen Fallback
    const getPersonScore = () => {
      // Prüfe ob das Element eine description mit Score hat (Ranker Users)
      if (item.description && item.description.includes('Score:')) {
        const scoreMatch = item.description.match(/Score:\s*(\d+)/);
        if (scoreMatch) {
          return parseInt(scoreMatch[1], 10);
        }
      }

      // Fallback für Mock-Elemente: Generiere eine konsistente Punktzahl basierend auf der ID
      const idSum = item.id
        .split('')
        .reduce((sum, char) => sum + char.charCodeAt(0), 0);
      return 500 + (idSum % 501); // Score zwischen 500 und 1000
    };

    const renderPersonContent = () => {
      // Verwende den echten Score aus dem Service
      const personScore = getPersonScore();

      return (
        <div className="person-container">
          <div className="person-avatar">
            <IonIcon
              src="/assets/icons/Person_Iro.svg"
              className="person-avatar-icon"
            />
          </div>
          <div className="person-info">
            <div className="person-header">
              <div className="title-section">
                <IonText className="person-name">{theTitle}</IonText>
                {isMockElement(item) && <div className="mock-pill">PROXY</div>}
              </div>
              <div className="person-score">
                <IonIcon icon={star} className="score-icon" />
                <IonText className="score-text">{personScore}</IonText>
              </div>
            </div>
            <div className="person-meta">
              {/* Location wenn vorhanden */}
              {item.location && (
                <div className="person-meta-item">
                  <IonIcon
                    icon={locationOutline}
                  />
                  <IonText>
                    {item.location}
                  </IonText>
                </div>
              )}

              {/* Country/Nationality wenn vorhanden */}
              {item.nationality && (
                <div className="person-meta-item">
                  <IonIcon icon={flagOutline} />
                  <IonText>
                    {item.nationality}
                  </IonText>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const renderContestContent = () => {
      // Contest-spezifische Daten
      let startDate = parseDateOrDefault(item.startDate);
      let endDate = parseDateOrDefault(item.endDate);
      // Sicherstellen, dass endDate immer nach startDate liegt
      if (endDate < startDate) {
        const tmp = endDate;
        endDate = startDate;
        startDate = tmp;
      }
      const maxRuns = (item as any).maxRuns || 3;

      // Contest Status basierend auf Datum
      const now = new Date();
      let contestStatus = 'upcoming';
      let statusText = (
        <Trans i18nKey="CONTEST_STATUS_UPCOMING">Bevorstehend</Trans>
      );

      if (startDate && endDate) {
        if (now >= startDate && now <= endDate) {
          contestStatus = 'active';
          statusText = <Trans i18nKey="CONTEST_STATUS_ACTIVE">Aktiv</Trans>;
        } else if (now > endDate) {
          contestStatus = 'finished';
          statusText = <Trans i18nKey="CONTEST_STATUS_FINISHED">Beendet</Trans>;
        }
      }

      // Zeige einen "Teilnehmer"-Pill, wenn der aktuelle User Teil des Contests ist
      const isUserParticipating = currentUser?.uid
        ? item.isUserParticipating(currentUser.uid)
        : false;

      return (
        <>
          {/* Header with Title, Mock Pill, Status und Teilnehmer-Pill */}
          <IonRow className="ion-justify-content-between ion-align-items-center">
            <div className="title-section">
              <IonText style={{ fontSize: 24, fontWeight: 'bold' }}>
                {theTitle}
              </IonText>
              {isMockElement(item) && <div className="mock-pill">PROXY</div>}
              {isUserParticipating && (
                <div className={`contest-status-badge active`}>
                  <Trans i18nKey="CONTEST_PARTICIPANT">Teilnehmer</Trans>
                </div>
              )}
              <div className={`contest-status-badge ${contestStatus}`}>
                {statusText}
              </div>
            </div>
          </IonRow>

          {/* Meta Information */}
          <div className="meta-info-container">
            {/* Contest Details */}
            <div className="contest-meta">
              <HStack className="meta-item">
                <IonIcon icon={calendarOutline} />
                <IonText>
                  {startDate.toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                  })}{' '}
                  {startDate.toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {' - '}
                  {endDate.toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                  })}{' '}
                  {endDate.toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </IonText>
              </HStack>

              <HStack className="meta-item">
                <IonIcon icon={locationOutline} />
                <IonText>
                  {item.location || 'Ort TBA'}
                </IonText>
              </HStack>

              <HStack className="meta-item">
                <IonIcon icon={trophy} />
                <IonText>Max. {maxRuns} Runs</IonText>
              </HStack>
            </div>
          </div>
        </>
      );
    };

    const renderContent = () => {
      const itemType = String(item.type).toLowerCase();

      if (itemType === 'event') {
        return renderEventContent();
      } else if (itemType === 'ranking') {
        return renderRankingContent();
      } else if (itemType === 'person') {
        return renderPersonContent();
      } else if (itemType === 'contest') {
        return renderContestContent();
      } else {
        // Fallback für andere Typen
        return (
          <>
            <IonRow className="ion-justify-content-between ion-align-items-center">
              <div className="title-section">
                <IonText style={{ fontSize: 24, fontWeight: 'bold' }}>
                  {theTitle}
                </IonText>
                {isMockElement(item) && <div className="mock-pill">PROXY</div>}
              </div>
            </IonRow>
          </>
        );
      }
    };

    // Bestimme die Klassen basierend auf dem Elementtyp
    const getItemClasses = () => {
      const baseClass = 'trick-list-item';
      const itemType = String(item.type).toLowerCase();

      if (itemType === 'person') {
        return `${baseClass} person-item`;
      } else if (itemType === 'ranking') {
        return `${baseClass} ranking-item`;
      } else if (itemType === 'event') {
        return `${baseClass} event-item`;
      } else if (itemType === 'contest') {
        return `${baseClass} contest-item`;
      }

      return baseClass;
    };

    return (
      <IonItem className={getItemClasses()}>
        <div
          style={{ height: '100%', width: '100%', padding: '16px 10px' }}
          onClick={() => {
            // Öffne das passende Modal basierend auf dem Elementtyp
            if (
              String(item.type).toLowerCase() === 'event' &&
              onOpenEventModal
            ) {
              onOpenEventModal(item);
            } else if (
              String(item.type).toLowerCase() === 'ranking' &&
              onOpenRankingModal
            ) {
              onOpenRankingModal(item);
            } else if (
              String(item.type).toLowerCase() === 'person' &&
              onOpenPersonModal
            ) {
              onOpenPersonModal(item, getPersonScore());
            } else if (
              String(item.type).toLowerCase() === 'contest' &&
              onOpenContestModal
            ) {
              onOpenContestModal(item);
            }
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
            }}
          >
            {renderContent()}
            {isLoading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                }}
              >
                <IonSpinner name="crescent" style={{ width: 48, height: 48 }} />
              </div>
            )}
          </div>
        </div>
      </IonItem>
    );
  }
);

export default ExploreListItem;
